import { useEffect, useState } from "react";
import { api, filterParams } from "@/lib/api";
import { InsightCard } from "@/components/InsightCard";
import { CollectiveVoice } from "@/components/CollectiveVoice";
import { VoiceWorkspace } from "@/components/VoiceWorkspace";
import GeoSpecialty from "@/pages/GeoSpecialty";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { POSITION_BG, TIER_BG, ACCESS_CLASS_BG, MATURITY_BG } from "@/lib/constants";
import { Search, ExternalLink, EyeOff, Eye, UserCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function InsightFeed({ filters, setFilters, role }) {
  const [insights, setInsights] = useState([]);
  const [themes, setThemes] = useState([]);
  const [search, setSearch] = useState("");
  const [period, setPeriod] = useState("all");
  const [sortBy, setSortBy] = useState("strategic");
  const [selected, setSelected] = useState(null);

  const isAdmin = role?.id === "admin";

  const IMPORTANCE = { Critical: 4, High: 3, Medium: 2, Low: 1 };
  const TIER = { High: 3, Moderate: 2, Emerging: 1 };
  const STANCE = { Supportive: 4, "Cautiously supportive": 3, "Neutral or observational": 2, Uncertain: 1, Questioning: 0, Conflicting: 0, Concerned: -1, Critical: -2 };
  const SORTS = {
    strategic: ["Most strategic", (a, b) => (IMPORTANCE[b.strategic_importance] || 0) - (IMPORTANCE[a.strategic_importance] || 0)],
    confidence: ["Highest confidence", (a, b) => (TIER[b.confidence_tier] || 0) - (TIER[a.confidence_tier] || 0)],
    recent: ["Most recent", (a, b) => (b.date || "").localeCompare(a.date || "")],
    challenging: ["Most challenging", (a, b) => (STANCE[a.position] ?? 2) - (STANCE[b.position] ?? 2)],
    supportive: ["Most supportive", (a, b) => (STANCE[b.position] ?? 2) - (STANCE[a.position] ?? 2)],
  };
  const sorted = [...insights].sort(SORTS[sortBy][1]);

  const dateFrom = () => {
    if (period === "all") return undefined;
    const d = new Date();
    d.setMonth(d.getMonth() - (period === "3m" ? 3 : 1));
    return d.toISOString().slice(0, 10);
  };

  const load = () => {
    api.get("/insights", { params: { ...filterParams(filters), search: search || undefined, date_from: filters.date_from || dateFrom(), date_to: filters.date_to || undefined } })
      .then((r) => setInsights(r.data.insights))
      .catch(() => toast.error("Could not load insights"));
  };

  useEffect(() => {
    api.get("/themes").then((r) => setThemes(r.data.themes));
  }, []);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, search, period]);

  const toggleAnonymise = async (insight, anonymised) => {
    const res = await api.post(`/admin/insights/${insight.id}/anonymise`, { anonymised });
    if (res.data.ok) {
      setSelected(res.data.insight);
      load();
      toast.success(anonymised ? "Speaker anonymised" : "Attribution restored");
    }
  };

  return (
    <div className="space-y-4" data-testid="insight-feed-view">
      <VoiceWorkspace
        themes={themes}
        activeTheme={filters.theme_id || "all"}
        onSelectTheme={(id) => setFilters({ ...filters, theme_id: id })}
      />
      <CollectiveVoice />
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="insight-search-input"
            placeholder="Search quotes, tags, roles…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 rounded-full bg-white border-slate-200 focus-visible:ring-teal-500"
          />
        </div>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="sort-filter" className="w-auto h-9 text-xs gap-1.5 rounded-full border-slate-200 bg-white">
            <span className="text-slate-400">Sort:</span>
            {SORTS[sortBy][0]}
          </SelectTrigger>
          <SelectContent>
            {Object.entries(SORTS).map(([k, [label]]) => <SelectItem key={k} value={k} data-testid={`sort-${k}`}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={period} onValueChange={setPeriod}>
          <SelectTrigger data-testid="date-filter" className="w-auto h-9 text-xs gap-1.5 rounded-full border-slate-200 bg-white">
            <span className="text-slate-400">Date:</span>
            {period === "all" ? "All time" : period === "3m" ? "Last 3 months" : "Last 30 days"}
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            <SelectItem value="3m">Last 3 months</SelectItem>
            <SelectItem value="1m">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <p className="text-xs text-slate-500 font-mono uppercase tracking-widest" data-testid="insight-count">{insights.length} source-linked insights</p>
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {sorted.map((i) => <InsightCard key={i.id} insight={i} onOpen={setSelected} />)}
      </div>
      {insights.length === 0 && <p className="text-sm text-slate-400 py-12 text-center" data-testid="insight-empty">No insights match the current filters.</p>}

      <div className="pt-2" data-testid="feed-comparisons">
        <h2 className="font-heading text-lg font-bold text-slate-900 mb-1">Segment, Geographic & Setting Comparison</h2>
        <p className="text-xs text-slate-500 mb-3">Reflects the current theme and filter selection.</p>
        <GeoSpecialty filters={filters} />
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl" data-testid="insight-detail-modal">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg pr-6">
                  {selected.attribution?.hcp_name
                    ? `${selected.attribution.hcp_name} — ${selected.attribution.institution}`
                    : `${selected.hcp_role} — ${selected.country}`}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200" data-testid="modal-illustrative-tag">Illustrative insight</Badge>
                <Badge variant="outline" className={POSITION_BG[selected.position]}>{selected.position}</Badge>
                <Badge variant="outline" className={MATURITY_BG[selected.maturity]}>{selected.maturity}</Badge>
                <Badge variant="outline" className={TIER_BG[selected.confidence_tier]}>{selected.confidence_tier} confidence</Badge>
                {selected.access_class && <Badge variant="outline" className={ACCESS_CLASS_BG[selected.access_class]}>{selected.access_class}</Badge>}
                <Badge variant="outline" className={selected.statement_type === "Direct quotation" ? "bg-teal-50 text-teal-700 border-teal-200" : "bg-rose-50 text-rose-700 border-rose-200"}>
                  {selected.statement_type}
                </Badge>
              </div>
              {selected.statement_type === "AI-generated paraphrase" ? (
                <div className="border border-rose-200 bg-rose-50/50 rounded-lg px-4 py-3" data-testid="modal-paraphrase-block">
                  <p className="font-mono text-[9px] uppercase tracking-widest text-rose-600 mb-1">AI-generated paraphrase — not a verbatim quotation</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{selected.quote}</p>
                </div>
              ) : (
                <blockquote className="border-l-2 border-teal-500 pl-4 text-slate-800 text-sm leading-relaxed italic bg-slate-50/70 rounded-r-lg py-2.5 pr-3">"{selected.quote}"</blockquote>
              )}
              {selected.governance?.in_review_queue && (
                <p className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="modal-verification-warning">
                  Pending verification — this statement is visible in the review queue but cannot support approved findings.
                </p>
              )}
              <div className="space-y-4 max-h-[52vh] overflow-y-auto pr-1">
                <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3" data-testid="attribution-panel">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2">
                      <UserCircle2 className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-xs font-semibold text-slate-800">
                          {selected.attribution?.hcp_name
                            ? `Named attribution: ${selected.attribution.hcp_name} · ${selected.attribution.institution} (illustrative example)`
                            : `Anonymised speaker · ${selected.hcp_role}`}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">{selected.attribution?.basis}</p>
                        {selected.attribution?.withheld && (
                          <p className="text-[10px] font-mono uppercase tracking-widest text-amber-600 mt-1" data-testid="attribution-withheld-tag">Attribution withheld — no verifiable public source or governed primary-research record</p>
                        )}
                        {selected.attribution?.anonymised_by_admin && (
                          <p className="text-[10px] font-mono uppercase tracking-widest text-amber-600 mt-1">Anonymised by administrator · source traceability retained</p>
                        )}
                      </div>
                    </div>
                    {isAdmin && selected.attribution?.type === "named" && (
                      <Button
                        data-testid="anonymise-toggle-btn"
                        size="sm" variant="outline"
                        className="h-7 text-xs shrink-0"
                        onClick={() => toggleAnonymise(selected, !selected.attribution.anonymised_by_admin)}
                      >
                        {selected.attribution.anonymised_by_admin
                          ? <><Eye className="w-3.5 h-3.5 mr-1" />Restore attribution</>
                          : <><EyeOff className="w-3.5 h-3.5 mr-1" />Anonymise speaker</>}
                      </Button>
                    )}
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">Why this view is held</p>
                  <p className="text-sm text-slate-600">{selected.why}</p>
                </div>
                <div data-testid="governance-grid">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Statement governance — required record</p>
                  <div className="bg-slate-50 border border-slate-200/80 rounded-lg p-3.5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2">
                    {[["Source type", selected.governance?.source_type], ["Speaker status", selected.governance?.speaker_status],
                      ["Specialty", selected.governance?.specialty], ["Country", selected.governance?.country],
                      ["Setting", selected.governance?.setting], ["Date", selected.governance?.date],
                      ["Statement type", selected.governance?.statement_type], ["Access class", selected.governance?.access_class],
                      ["Transcript / source passage", selected.governance?.source_passage], ["Consent / permitted use", selected.governance?.consent_status],
                      ["Reviewer", selected.governance?.reviewer], ["Verification status", selected.governance?.verification_status]].map(([label, value]) => (
                      <div key={label}>
                        <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400">{label}</p>
                        <p className={`text-[11px] leading-snug ${label === "Verification status" && value !== "Verified" ? "text-amber-700 font-semibold" : "text-slate-700"}`}>{value || "—"}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {selected.governance?.minimums && (
                  <div data-testid="minimums-grid">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">
                      Governance minimum — {selected.governance.minimums.record_class}
                      {selected.governance.minimums.complete
                        ? <span className="ml-2 text-teal-600 normal-case tracking-normal font-sans font-semibold">Complete — quoted use permitted</span>
                        : <span className="ml-2 text-amber-700 normal-case tracking-normal font-sans font-semibold">Incomplete — quoted use blocked</span>}
                    </p>
                    <div className={`border rounded-lg p-3.5 grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 ${selected.governance.minimums.complete ? "bg-slate-50 border-slate-200/80" : "bg-amber-50/50 border-amber-200"}`}>
                      {selected.governance.minimums.required.map((k) => (
                        <div key={k}>
                          <p className="font-mono text-[8.5px] uppercase tracking-widest text-slate-400">{k.replace(/_/g, " ")}</p>
                          <p className={`text-[11px] leading-snug ${selected.governance.minimums.fields[k] ? "text-slate-700" : "text-amber-700 font-semibold"}`}>
                            {selected.governance.minimums.fields[k] || "Missing — blocks quoted use"}
                          </p>
                        </div>
                      ))}
                    </div>
                    {selected.governance.quoted_use_note && (
                      <p className="text-[11px] text-amber-700 mt-1.5 leading-snug" data-testid="quoted-use-note">{selected.governance.quoted_use_note}</p>
                    )}
                  </div>
                )}
                <div data-testid="analytical-grid">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-2">Analytical dimensions</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {[
                      ["Clinical context", [
                        ["Subtheme", selected.subtheme],
                        ["Clinical relevance", selected.clinical_relevance],
                        ["Line of therapy", selected.treatment_line],
                        ["Treatment context", selected.treatment_context],
                        ["Patient population", selected.patient_population],
                        ["Biomarker context", selected.biomarker_context],
                      ]],
                      ["Evidence context", [
                        ["Evidence quality", selected.evidence_quality],
                        ["Novelty", selected.novelty],
                        ["Interpretation confidence", selected.interpretation_confidence],
                      ]],
                      ["Source context", [
                        ["Source reliability", selected.source_reliability],
                        ["Example HCP segment", selected.hcp_segment],
                        ["Practice setting", selected.practice_setting],
                        ["Geography", `${selected.geography} · ${selected.country}`],
                      ]],
                      ["Strategic interpretation", [
                        ["Strategic importance", selected.strategic_importance],
                        ["Strength of conviction", selected.conviction],
                      ]],
                    ].map(([group, rows]) => (
                      <div key={group} className="bg-slate-50 border border-slate-200/80 rounded-lg p-3.5">
                        <p className="font-mono text-[9px] uppercase tracking-widest text-teal-700 mb-2">{group}</p>
                        <div className="space-y-1.5">
                          {rows.map(([label, value]) => (
                            <div key={label} className="flex items-baseline justify-between gap-3">
                              <p className="text-[10.5px] text-slate-400 shrink-0">{label}</p>
                              <p className="text-xs text-slate-700 font-medium leading-snug text-right">{value || "—"}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="bg-indigo-50/60 border border-indigo-100 rounded-lg p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-indigo-700 mb-1">Implications for treatment adoption</p>
                    <p className="text-xs text-slate-700 leading-snug">{selected.adoption_impact}</p>
                  </div>
                  <div className="bg-teal-50/60 border border-teal-100 rounded-lg p-3">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-teal-700 mb-1">Recommended stakeholder action</p>
                    <p className="text-xs text-slate-700 leading-snug">{selected.recommended_action}</p>
                  </div>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Evidence base</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selected.evidence_refs.map((r) => (
                      <a key={r.label} href={r.url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-full px-2.5 py-1 hover:bg-indigo-100">
                        <ExternalLink className="w-3 h-3" />{r.label}
                      </a>
                    ))}
                  </div>
                </div>
                <p className="text-xs text-slate-500">Source: {selected.source_type} — {selected.source_name} · {selected.date} · <span className={selected.governance?.verification_status === "Verified" ? "text-teal-600" : "text-amber-600"}>{selected.governance?.verification_status || "—"}</span></p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
